import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Box, Typography, Grid2, styled } from "@mui/material";
import Chart from "react-apexcharts";

// Features & Components
import CartLoader from "../../../Helper/Loaders/CartLoader";
import {
    getFleetPerformanceAnalyticsTimeseries,
    getFleetPerformanceAnalyticsList,
    getFleetPerformanceAnalyticsListActions
} from "../../../Api/FleetPerformanceApi";
import {
    formatDateLabel,
    formatDateForAPI,
    formatDateRange,
} from "../../../Helper/Analytics/AnalyticsHelper";
import RenderChartLegend from "../../AdvancedAnalytics/ContinuousChart/RenderChartLegend";
import { calculateGranularity } from "../../BmsFaults/CommonFunctions";
import { calculateNiceScale, generateYLabelsWithPositions } from "../../BmsFaults/Analytics/ScrollableColumnChart/ChartScaleUtils";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { encryptTheParams } from "../../../Helper/QueryParams/EncryptDecrypt";
import { parseDateRangeFromLabel } from "../../../Helper/Analytics/AnalyticsHelper";
import { useFleetChartDrillDown } from "../Components/FleetChartDrillDown/useFleetChartDrillDown";
import FleetChartDrillDownModal from "../Components/FleetChartDrillDown/FleetChartDrillDownModal";
import { FLEET_CHART_CONFIG } from "./FleetChartConfig";
import { formatCompactEnergy, formatCompactNumber } from "../Components/FleetCommonFunctions";
import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import DeviceIDLink from "../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/DeviceIDLink";
import DigitalTwinTableSearch from "../../../Components/DigitalTwinTableSearch";
import FleetDetailsSidebar from "../Components/FleetDetailsSidebar";
import FleetChartLegend from "../Components/FleetChartLegend";
import {
    FLEET_PERFORMANCE_TABLE_KEYS,
    useFleetTableConfig,
} from "../fleetPerformanceTableConfig";
import { useSearchableFields, searchFieldsKey } from "../../../Hooks/usePagesContent";

// --- Local Styled Components (Isolating from shared BmsFaults logic) ---

const BMSFaultChartWrapper = styled(Box)(({ drillDownEnabled }) => ({
    "& .apexcharts-series path": {
        cursor: drillDownEnabled ? "pointer" : "default",
    },
    "& .apexcharts-bar-area": {
        cursor: drillDownEnabled ? "pointer" : "default",
    }
}));

// --- Local Sub-components ---

const FixedColumn = ({
    position = "left",
    label,
    labelData = [],
    chartHeight = 315,
    width = 80 // Increased width from 70 to 80
}) => {
    const isLeft = position === "left";
    const spacing = "20px"; // Increased spacing from 16px to 20px

    return (
        <Box
            sx={{
                width: `${width}px`,
                flexShrink: 0,
                zIndex: 10,
                backgroundColor: "white",
                display: "flex",
                gap: "2px",
                flexDirection: isLeft ? "row" : "row-reverse",
            }}
        >
            {/* Axis Label */}
            <Box
                sx={{
                    fontSize: "12px",
                    color: "#686868",
                    fontWeight: 400,
                    writingMode: "vertical-rl",
                    transform: isLeft ? "rotate(180deg)" : "rotate(0deg)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "20px",
                }}
            >
                {label}
            </Box>

            {/* Y-axis Labels */}
            <Box
                sx={{
                    height: `${chartHeight}px`,
                    position: "relative",
                    flex: 1,
                }}
            >
                {labelData.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            fontSize: "12px",
                            color: "#686868",
                            textAlign: isLeft ? "right" : "left",
                            paddingRight: isLeft ? spacing : "0px",
                            paddingLeft: isLeft ? "0px" : spacing,
                            fontFamily: "Helvetica, Arial, sans-serif",
                            position: "absolute",
                            top: `${item.position}px`,
                            [isLeft ? "right" : "left"]: 0,
                            transform: "translateY(-50%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: isLeft ? "flex-end" : "flex-start",
                        }}
                    >
                        {item.value}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const ScrollableChartWrapper = ({
    chartOptions,
    series,
    chartType = "bar",
    chartHeight = 315,
    leftAxis = null,
    rightAxis = null,
    isSidebarExpanded = false,
    minWidth = 800,
    categoriesCount = 0,
    drillDownEnabled = false
}) => {
    const getMaxWidth = () => {
        if (isSidebarExpanded) return "calc(100vw - 420px)";
        return rightAxis ? "calc(100vw - 340px)" : "calc(100vw - 250px)"; // Adjusted for 80px FixedColumn width
    };

    return (
        <Box sx={{ display: "flex", position: "relative", width: "100%", overflow: "hidden" }}>
            {leftAxis && (
                <FixedColumn
                    position="left"
                    label={leftAxis.label}
                    labelData={leftAxis.labelData}
                    chartHeight={chartHeight}
                />
            )}

            <Box
                sx={{
                    flex: 1,
                    overflowX: "auto",
                    overflowY: "hidden",
                    position: "relative",
                    minWidth: 0,
                    maxWidth: getMaxWidth(),
                }}
            >
                <BMSFaultChartWrapper
                    drillDownEnabled={drillDownEnabled}
                    sx={{
                        width: `max(100%, ${minWidth}px)`,
                        height: chartHeight,
                    }}
                >
                    <Chart
                        options={chartOptions}
                        series={series}
                        type={chartType}
                        height={chartHeight}
                        width="100%"
                    />
                </BMSFaultChartWrapper>
            </Box>

            {rightAxis && (
                <FixedColumn
                    position="right"
                    label={rightAxis.label}
                    labelData={rightAxis.labelData}
                    chartHeight={chartHeight}
                />
            )}
        </Box>
    );
};

// --- Constants & Config for Details Table ---

const SEARCHABLE_FIELDS = [
    { key: "licensePlateNo", title: "Bus Number" },
    { key: "deviceID", title: "Device ID" },
    { key: "vin", title: "Chassis Number" },
];

const TABLE_COLUMNS_CONFIG = {
    licencePlatNumber: { title: "Bus Number", headerLabel: "Bus Number", dataIndex: "licencePlatNumber", key: "licencePlatNumber", width: 200, valueRenderingType: "device-id-link", fixed: "left" },
    ChargeEnergyConsumedTotal: { title: "Charge Energy Consumed (kWh)", headerLabel: "Charge Energy Consumption (kWh)", dataIndex: "ChargeEnergyConsumedTotal", key: "ChargeEnergyConsumedTotal", width: 220, valueRenderingType: "number", sortDirections: ["ascend", "descend", "ascend"], sorter: true },
    DischargeEnergyConsumedTotal: { title: "Discharge Energy Consumed (kWh)", headerLabel: "Discharge Energy Consumption (kWh)", dataIndex: "DischargeEnergyConsumedTotal", key: "DischargeEnergyConsumedTotal", width: 220, valueRenderingType: "number", sortDirections: ["ascend", "descend", "ascend"], sorter: true },
    RegenEnergyTotal: { title: "Regeneration Energy (kWh)", headerLabel: "Regeneration Energy (kWh)", dataIndex: "RegenEnergyTotal", key: "RegenEnergyTotal", width: 220, valueRenderingType: "number", sortDirections: ["ascend", "descend", "ascend"], sorter: true },
    DistanceTotal: { title: "Distance (km)", headerLabel: "Distance (km)", dataIndex: "DistanceTotal", key: "DistanceTotal", width: 150, valueRenderingType: "number", sortDirections: ["ascend", "descend", "ascend"], sorter: true },
    originDepot: { title: "Depot", headerLabel: "Depot", dataIndex: "originDepot", key: "originDepot", width: 150, textFormat: "title", sortDirections: ["ascend", "descend", "ascend"], sorter: true },
    originCity: { title: "City", headerLabel: "City", dataIndex: "originCity", key: "originCity", width: 150, textFormat: "title", sortDirections: ["ascend", "descend", "ascend"], sorter: true },
    originState: { title: "State", headerLabel: "State", dataIndex: "originState", key: "originState", width: 150, textFormat: "title", sortDirections: ["ascend", "descend", "ascend"], sorter: true }
};

const TABLE_COLUMN_ORDER = ["licencePlatNumber", "ChargeEnergyConsumedTotal", "DischargeEnergyConsumedTotal", "RegenEnergyTotal", "DistanceTotal", "originDepot", "originCity", "originState"];

/**
 * The FleetPerformance org meta key that can replace this details table's
 * columns. Absent from an org's meta, the config above stands unchanged.
 */
const ORG_META_TABLE_KEY = FLEET_PERFORMANCE_TABLE_KEYS.ENERGY_FLOW_TRENDS_DETAILS;

// --- Sub-Component: Details Table ---

const EnergyFlowDetailsTable = ({ startDate, endDate, globalFilters, searchTerm, appliedFilters }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { tableColumnsConfig, tableColumnOrder, resolveMetaInfo } =
        useFleetTableConfig(ORG_META_TABLE_KEY, TABLE_COLUMNS_CONFIG, TABLE_COLUMN_ORDER);

    const [initialColumns, setInitialColumns] = useState([]);
    const [tableColumns, setTableColumns] = useState([]);
    const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);
    const [columnOrder, setColumnOrder] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [sortBy, setSortBy] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isSelectAll, setIsSelectAll] = useState(false);

    // Keyed on the filters' CONTENT, not the object: the parent builds the
    // globalFilters spread inline, so a fresh identity every render would reset
    // the page on things like collapsing the sidebar. Without it, changing the
    // customer or location scope from the sidebar left the request asking for
    // page 3 of a result set that had just changed underneath it.
    const globalFiltersKey = JSON.stringify(globalFilters);
    useEffect(() => {
        setPageNumber(1);
    }, [searchTerm, appliedFilters, globalFiltersKey]);

    const onBatteryIDClick = (record) => {
        encryptTheParams({ batteryID: record.id, deviceID: record.deviceID, prevLink: '/fleet-performance', navigateFromTab: true }, navigate, true);
    };

    const customRenderers = {
        'device-id-link': (record, config) => <DeviceIDLink record={record} config={config} onTextClick={() => onBatteryIDClick(record)} />,
    };

    useEffect(() => {
        const availableColumns = getColumnsConfig({ tableColumns: tableColumnsConfig, tableColumnOrder, customRenderers, t });
        setInitialColumns(availableColumns);
        setTableColumns(availableColumns);
        setSelectedColumnKeys(availableColumns.map(col => col.dataIndex));
        setColumnOrder(availableColumns.map(col => col.headerLabel));
    }, [t, tableColumnsConfig, tableColumnOrder]);

    const applySelectedColumns = (newSelectedKeys) => {
        const filteredColumns = initialColumns.filter((columnConfig) =>
            newSelectedKeys.includes(columnConfig.dataIndex)
        );
        setTableColumns(filteredColumns);
        setSelectedColumnKeys(newSelectedKeys);
        setColumnOrder(filteredColumns.map((col) => col.headerLabel));
    };

    const onColumnOrderChange = (newTableColumns) => {
        setColumnOrder(newTableColumns.map((col) => col.headerLabel));
    };

    const prevDepsRef = useRef('');
    useEffect(() => {
        const fetchData = async () => {
            const currentDeps = JSON.stringify({ startDate, endDate, globalFilters, searchTerm, pageNumber, rowsPerPage, sortBy, appliedFilters, metaInfo: resolveMetaInfo(['licencePlatNumber', 'originCity', 'originState', 'originDepot', 'deviceID', 'pingStatus']) });
            if (prevDepsRef.current === currentDeps) return;
            prevDepsRef.current = currentDeps;

            setIsLoading(true);
            try {
                const searchID = searchTerm.selectedSearchColumn || "batteryID";
                const filters = (searchTerm?.selectedDevices?.length > 0) ? [{ name: searchID || 'batteryID', values: searchTerm.selectedDevices }] : [];
                if (appliedFilters?.length > 0) filters.push(...appliedFilters);

                const apiParams = {
                    groupBy: ['batteryID'],
                    select: ['ChargeEnergyConsumedTotal', 'DischargeEnergyConsumedTotal', 'RegenEnergyTotal', 'DistanceTotal'],
                    metaInfo: resolveMetaInfo(['licencePlatNumber', 'originCity', 'originState', 'originDepot', 'deviceID', 'pingStatus']),
                    startDate: formatDateForAPI(startDate),
                    endDate: formatDateForAPI(endDate),
                    skipPagination: false,
                    filters,
                    pageNum: pageNumber,
                    pageCount: rowsPerPage,
                };
                if (sortBy.length > 0) apiParams.sortBy = JSON.stringify(sortBy);

                const response = await getFleetPerformanceAnalyticsList(globalFilters, apiParams);
                if (response?.responseStatus?.code === 200) {
                    const rawData = response.response?.data || [];
                    const data = rawData.map((item, index) => {
                        const getMetric = (type) => item.metricsArray?.find(m => m.type === type)?.value || 0;
                        return {
                            id: item.xLabel || index,
                            ...item.metaInfo,
                            ChargeEnergyConsumedTotal: getMetric('ChargeEnergyConsumedTotal'),
                            DischargeEnergyConsumedTotal: getMetric('DischargeEnergyConsumedTotal'),
                            RegenEnergyTotal: getMetric('RegenEnergyTotal'),
                            DistanceTotal: getMetric('DistanceTotal')
                        };
                    });
                    setTableData(data);
                    setTotal(response.response?.paginationInfo?.total || data.length);
                }
            } catch (error) { console.error("Error fetching energy flow table data:", error); }
            finally { setIsLoading(false); }
        };
        fetchData();
    }, [startDate, endDate, globalFilters, searchTerm, pageNumber, rowsPerPage, sortBy, appliedFilters, tableColumnOrder, resolveMetaInfo]);

    const applyTableAction = async (actionType, extraParams = {}) => {
        const searchID = searchTerm?.selectedSearchColumn || "batteryID";
        const filters = (searchTerm?.selectedDevices?.length > 0) ? [{ name: searchID, values: searchTerm.selectedDevices }] : [];
        if (appliedFilters?.length > 0) filters.push(...appliedFilters);

        const columnsConfig = [
            { dataIndex: "licencePlatNumber", headerLabel: "Bus Number" },
            { dataIndex: "ChargeEnergyConsumedTotal", headerLabel: "Charge Energy Consumed (kWh)" },
            { dataIndex: "DischargeEnergyConsumedTotal", headerLabel: "Discharge Energy Consumed (kWh)" },
            { dataIndex: "RegenEnergyTotal", headerLabel: "Regeneration Energy (kWh)" },
            { dataIndex: "DistanceTotal", headerLabel: "Distance (km)" },
            { dataIndex: "originDepot", headerLabel: "Depot" },
            { dataIndex: "originCity", headerLabel: "City" },
            { dataIndex: "originState", headerLabel: "State" },
        ];

        const apiParams = {
            actionType,
            groupBy: ['batteryID'],
            select: ['ChargeEnergyConsumedTotal', 'DischargeEnergyConsumedTotal', 'RegenEnergyTotal', 'DistanceTotal'],
            metaInfo: resolveMetaInfo(['licencePlatNumber', 'originCity', 'originState', 'originDepot', 'deviceID']),
            startDate: formatDateForAPI(startDate),
            endDate: formatDateForAPI(endDate),
            filters,
            resourceIDs: isSelectAll ? [] : selectedRowKeys,
            selectAll: isSelectAll,
            columns: columnsConfig,
            columnOrder: columnOrder.length > 0 ? columnOrder : columnsConfig.map(c => c.headerLabel),
            ...(sortBy.length > 0 ? { sortBy: JSON.stringify(sortBy) } : {}),
            ...(actionType === "mail" && {
                emailTo: extraParams.emailTo,
                emailCc: extraParams.emailCc,
                subject: extraParams.subject,
                body: extraParams.body,
            }),
        };

        const response = await getFleetPerformanceAnalyticsListActions(globalFilters, apiParams);
        if (response?.responseStatus?.code === 200) {
            return response.response?.data || (actionType === "download" ? [] : null);
        }
        return actionType === "download" ? [] : null;
    };

    const getCSVData = async () => {
        return applyTableAction("download");
    };

    const onSendEmail = async (emailParams) => {
        return applyTableAction("mail", emailParams);
    };

    return (
        <GenericDraggableTableView
            title="Vehicle List"
            count={total}
            tableColumns={tableColumns}
            initialColumns={initialColumns}
            selectedColumns={selectedColumnKeys}
            applySelectedColumns={applySelectedColumns}
            onColumnOrderChange={onColumnOrderChange}
            columnOrder={columnOrder}
            tableData={tableData}
            isLoading={isLoading}
            pageNumber={pageNumber}
            rowsPerPage={rowsPerPage}
            total={total}
            onPageChange={setPageNumber}
            applySort={(key, order) => { setSortBy([{ column: key, direction: order === "asc" ? "ASC" : "DESC" }]); setPageNumber(1); }}
            emptyMessage="No vehicles found"
            filters={{}}
            disableSearch={true}
            showPrimaryButton={false}
            containerStyles={{ height: "auto" }}
            tableScroll={{ x: "max-content" }}
            rowSelection={{
                type: "checkbox",
                selectedRowKeys,
                isSelectAll,
                onChange: (keys) => {
                    setSelectedRowKeys(keys);
                },
                onSelect: (_record, selected) => {
                    if (!selected) setIsSelectAll(false);
                },
                onSelectAll: (selected) => {
                    setIsSelectAll(selected);
                    if (!selected) setSelectedRowKeys([]);
                }
            }}
            getTotalRowSelectionCount={() => isSelectAll ? total : selectedRowKeys.length}
            getTotalRowSelection={() => selectedRowKeys}
            onClearSelection={() => {
                setSelectedRowKeys([]);
                setIsSelectAll(false);
            }}
            getCSVData={getCSVData}
            onSendEmail={onSendEmail}
            getCSVFileName={() => `energy-flow-details-${startDate}-${endDate}.csv`}
        />
    );
};

// --- Sub-Component: Details View ---

export const EnergyFlowDetailsView = ({ onBack, startDate: initialStartDate, endDate: initialEndDate, initialGranularity, allFleets, allOnboardingLocationsHierarchy, globalFilters }) => {
    const searchFields = useSearchableFields(SEARCHABLE_FIELDS);
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(initialEndDate);
    const [granularity, setGranularity] = useState(initialGranularity || "Weekly");
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [localHierarchicalLocation, setLocalHierarchicalLocation] = useState(globalFilters?.hierarchicalLocation);
    // The sidebar's Business Segment / Customer Account panels both resolve to fleet
    // ids, which is what becomes the fleetIDs request header.
    const [localFleetIds, setLocalFleetIds] = useState(globalFilters?.fleets);
    const [searchTerm, setSearchTerm] = useState({ selectedDevices: [], selectedSearchColumn: null });

    return (
        <Box sx={{ display: "flex", gap: 3, height: "100%", position: "relative" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: isSidebarExpanded ? "calc(100% - 320px)" : "100%", minWidth: 0, transition: "width 0.3s ease-in-out" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", "&:hover": { "& .MuiTypography-root": { color: "#6D72F6 !important" }, "& .MuiSvgIcon-root": { color: "#6D72F6" } } }} onClick={onBack}>
                        <ChevronLeftIcon sx={{ fontSize: 20, color: "#2E2E2E" }} />
                        <Typography variant="fw500s14lh24c2E2E2E">Back</Typography>
                    </Box>
                </Box>
                <DigitalTwinTableSearch onSearch={(devs, col) => setSearchTerm({ selectedDevices: devs, selectedSearchColumn: col })} containerWidth="80%" key={searchFieldsKey(searchFields)} searchableFields={searchFields} />
                <Box sx={{ backgroundColor: "#FFFFFF", borderRadius: "8px", padding: "24px 28px", border: "1px solid #E6E6E6" }}>
                    <Box sx={{ mb: 1 }}><Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>Energy Flow Trend</Typography></Box>
                    <EnergyFlowTrendsView startDate={startDate} endDate={endDate} granularity={granularity} globalFilters={{ ...globalFilters, fleets: localFleetIds, hierarchicalLocation: localHierarchicalLocation }} appliedFilters={appliedFilters} searchTerm={searchTerm} enableDrillDown={false} />
                </Box>
                <EnergyFlowDetailsTable startDate={startDate} endDate={endDate} globalFilters={{ ...globalFilters, fleets: localFleetIds, hierarchicalLocation: localHierarchicalLocation }} searchTerm={searchTerm} appliedFilters={appliedFilters} />
            </Box>
            <FleetDetailsSidebar isExpanded={isSidebarExpanded} onToggle={() => setIsSidebarExpanded(!isSidebarExpanded)} startDate={startDate} endDate={endDate} onDateChange={({ startDate, endDate }) => { setStartDate(startDate); setEndDate(endDate); }} granularity={granularity} onGranularityChange={setGranularity} allFleets={allFleets} allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy} selectedFleetIds={localFleetIds || []} selectedHierarchicalLocation={localHierarchicalLocation} onFiltersChange={setAppliedFilters} onHierarchicalLocationChange={setLocalHierarchicalLocation} onFleetIdsChange={setLocalFleetIds} />
        </Box>
    );
};

// --- Trend Chart Components ---

const EnergyFlowTrendsView = ({ startDate, endDate, globalFilters, appliedFilters = [], granularity, isSidebarExpanded = false, enableDrillDown = true, searchTerm }) => {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState({
        categories: [],
        rawCategories: [],
        chargeData: [],
        dischargeData: [],
        regenData: [],
        distanceData: [],
        dateRanges: []
    });

    const prevDepsRef = useRef('');

    useEffect(() => {
        const fetchChartData = async () => {
            const currentDeps = JSON.stringify({ startDate, endDate, globalFilters, appliedFilters, granularity, searchTerm });
            if (prevDepsRef.current === currentDeps) return;
            prevDepsRef.current = currentDeps;

            try {
                setLoading(true);
                const granularityValue = (granularity || calculateGranularity(startDate, endDate)).toUpperCase();
                const filters = [...(appliedFilters || [])];
                const searchID = searchTerm?.selectedSearchColumn || 'batteryID';
                if (searchTerm?.selectedDevices?.length > 0) {
                    filters.push({ name: searchID, values: searchTerm.selectedDevices });
                }

                const apiParams = {
                    groupBy: [],
                    select: ['ChargeEnergyConsumedTotal', 'DischargeEnergyConsumedTotal', 'RegenEnergyTotal', 'DistanceTotal'],
                    granularity: granularityValue,
                    startDate: formatDateForAPI(startDate),
                    endDate: formatDateForAPI(endDate),
                    filters,
                };

                const response = await getFleetPerformanceAnalyticsTimeseries(globalFilters, apiParams);
                if (response?.responseStatus?.code === 200) {
                    const data = response.response?.data || [];
                    const categories = [];
                    const chargeData = [];
                    const dischargeData = [];
                    const regenData = [];
                    const distanceData = [];
                    const dateRanges = [];

                    data.forEach(item => {
                        categories.push(formatDateLabel(item.xLabel));
                        dateRanges.push(item.xLabel);

                        let charge = 0, discharge = 0, regen = 0, distance = 0;
                        (item.metricsArray || []).forEach(m => {
                            if (m.type === "ChargeEnergyConsumedTotal") charge = m.value;
                            else if (m.type === "DischargeEnergyConsumedTotal") discharge = m.value;
                            else if (m.type === "RegenEnergyTotal") regen = m.value;
                            else if (m.type === "DistanceTotal") distance = m.value;
                        });

                        chargeData.push(charge);
                        dischargeData.push(discharge);
                        regenData.push(regen);
                        distanceData.push(distance);
                    });

                    setChartData({ categories, rawCategories: dateRanges, chargeData, dischargeData, regenData, distanceData, dateRanges });
                }
            } catch (error) {
                console.error("Error fetching energy flow trend data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchChartData();
    }, [startDate, endDate, globalFilters, appliedFilters, granularity]);

    const allEnergyValues = [...chartData.chargeData, ...chartData.dischargeData, ...chartData.regenData].filter(v => v > 0);
    const energyMin = allEnergyValues.length > 0 ? Math.min(...allEnergyValues) : 0;
    const { unit: energyUnit, divisor: energyDivisor } = formatCompactEnergy(energyMin);
    const distanceMax = Math.max(...chartData.distanceData, 0);

    const scaledChargeData = chartData.chargeData.map(v => Math.round(v / energyDivisor));
    const scaledDischargeData = chartData.dischargeData.map(v => Math.round(v / energyDivisor));
    const scaledRegenData = chartData.regenData.map(v => Math.round(v / energyDivisor));

    const scaledEnergyMax = Math.max(...scaledChargeData, ...scaledDischargeData, ...scaledRegenData, 0);

    const energyScale = calculateNiceScale(0, scaledEnergyMax, 6);
    const distanceScale = calculateNiceScale(0, distanceMax, 6);

    const energyLabels = generateYLabelsWithPositions(energyScale, 315, 30, 37);
    const distanceLabels = generateYLabelsWithPositions(distanceScale, 315, 30, 37)
        .map(item => ({ ...item, value: formatCompactNumber(item.value) }));

    const chartOptions = {
        chart: {
            id: "energy-flow-trend-chart",
            type: "bar",
            height: 315,
            toolbar: { show: false },
            zoom: { enabled: false },
            events: enableDrillDown ? {
                dataPointSelection: (event, chartContext, config) => {
                    const { dataPointIndex, seriesIndex } = config;
                    if (dataPointIndex === -1 || seriesIndex === -1) return;

                    const seriesName = series[seriesIndex].name;
                    const xLabel = chartData.categories[dataPointIndex];
                    const rawDate = chartData.rawCategories[dataPointIndex];
                    const { startDate: parsedStart, endDate: parsedEnd } = parseDateRangeFromLabel(rawDate);

                    drillDown.handleDataPointClick({
                        xLabel,
                        seriesName,
                        startDate: formatDateForAPI(parsedStart),
                        endDate: formatDateForAPI(parsedEnd),
                    });
                }
            } : {}
        },
        stroke: {
            width: [0, 0, 0, 3],
            curve: 'straight'
        },
        markers: {
            size: [0, 0, 0, 3], // Show markers only for line chart (Vehicles Affected)
            strokeWidth: 0,
        },
        plotOptions: {
            bar: {
                columnWidth: '24px',
                borderRadius: 2
            }
        },
        colors: ["#D47CAF", "#7A3CC9", "#0C7A1F", "#1976D2"],
        xaxis: {
            categories: chartData.categories,
            tickPlacement: 'between',
            axisBorder: { show: false },
            axisTicks: { show: false },
            tooltip: { enabled: false },
            labels: {
                style: {
                    colors: '#8F8F8F',
                    fontSize: '12px'
                }
            }
        },
        yaxis: [
            {
                show: false,
                min: energyScale.min,
                max: energyScale.max,
                tickAmount: 5,
            },
            {
                show: false,
                min: energyScale.min,
                max: energyScale.max,
                tickAmount: 5,
            },
            {
                show: false,
                min: energyScale.min,
                max: energyScale.max,
                tickAmount: 5,
            },
            {
                show: false,
                min: distanceScale.min,
                max: distanceScale.max,
                tickAmount: 5,
            }
        ],
        grid: {
            borderColor: "#F0F0F0",
            strokeDashArray: 3,
            padding: {
                left: 30, // Added padding to prevent leftmost bar clipping
                right: 30
            },
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } }
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        tooltip: {
            shared: true,
            intersect: false,
            followCursor: true,
            y: {
                formatter: (val, { seriesIndex }) => {
                    if (val === undefined || val === null) return val;
                    if (seriesIndex === 3) return val?.toFixed(1) + " km";
                    return val?.toFixed(1) + ` ${energyUnit}`;
                }
            }
        }
    };

    const drillDown = useFleetChartDrillDown({
        startDate,
        endDate,
        globalFilters,
        config: FLEET_CHART_CONFIG.ENERGY_FLOW_TRENDS,
    });

    const series = [
        { name: `Charge Energy Consumption (${energyUnit})`, type: "bar", data: scaledChargeData },
        { name: `Discharge Energy Consumption (${energyUnit})`, type: "bar", data: scaledDischargeData },
        { name: `Regeneration Energy Recovered (${energyUnit})`, type: "bar", data: scaledRegenData },
        { name: "Distance Covered (km)", type: "line", data: chartData.distanceData }
    ];

    if (loading) return <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 365 }}><CartLoader /></Box>;

    if (!chartData.categories || chartData.categories.length === 0) {
        return (
            <Box sx={{ height: 365, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Typography variant="fw400s14c686868">No chart data available</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 2, width: "100%" }}>
            <Box sx={{ mb: 2 }}>
                <RenderChartLegend
                    legendSeries={[
                        { name: `Charge Energy Consumption (${energyUnit})`, color: "#D47CAF" },
                        { name: `Discharge Energy Consumption (${energyUnit})`, color: "#7A3CC9" },
                        { name: `Regeneration Energy Recovered (${energyUnit})`, color: "#0C7A1F" },
                        { name: "Distance Covered (km)", color: "#1976D2" }
                    ]}
                    chartIDs={["energy-flow-trend-chart"]}
                    centered={true}
                />
            </Box>

            <ScrollableChartWrapper
                chartOptions={chartOptions}
                series={series}
                chartType="bar"
                chartHeight={315}
                leftAxis={{ label: `Energy (${energyUnit})`, labelData: energyLabels }}
                rightAxis={{ label: "Distance Covered (km)", labelData: distanceLabels }}
                isSidebarExpanded={isSidebarExpanded}
                categoriesCount={chartData.categories.length}
                minWidth={chartData.categories.length * 100}
                drillDownEnabled={enableDrillDown}
            />

            <FleetChartDrillDownModal
                open={drillDown.modalOpen}
                onClose={drillDown.handleModalClose}
                selectedDataPoint={drillDown.selectedDataPoint}
                getTableData={drillDown.getTableData}
                applyTableAction={drillDown.applyTableAction}
                getCSVFileName={drillDown.getCSVFileName}
                tableConfigs={drillDown.getTableConfig(1, drillDown.selectedDataPoint)}
                level2GetTableData={drillDown.level2GetTableData}
                level2ApplyTableAction={drillDown.level2ApplyTableAction}
                level2GetCSVFileName={drillDown.level2GetCSVFileName}
                level2TableConfigs={drillDown.getTableConfig(2, drillDown.selectedDataPoint)}
                title={drillDown.getModalTitle}
                level2Title={drillDown.level2Title}
            />
        </Box>
    );
};

const EnergyFlowTrends = ({ onShowDetails, startDate, endDate, globalFilters }) => {
    return (
        <Box
            sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                padding: "24px 22px",
                border: "1px solid #E6E6E6",
                maxWidth: 'calc(100vw - 102px)'
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography
                        variant="fw600s16lh24c444B51"
                        sx={{ color: "#2E2E2E" }}
                    >
                        Energy Flow Trend
                    </Typography>
                    {onShowDetails && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                cursor: "pointer",
                                "&:hover": {
                                    "& .MuiTypography-root": { color: "#4D44D8" },
                                    "& .MuiSvgIcon-root": { color: "#4D44D8" },
                                },
                            }}
                            onClick={onShowDetails}
                        >
                            <Typography
                                variant="fw500s12lh20c000000"
                                sx={{ color: "#6D72F6" }}
                            >
                                Details
                            </Typography>
                            <ChevronRightIcon sx={{ color: "#6D72F6", fontSize: "18px" }} />
                        </Box>
                    )}
                </Box>
                <Typography variant="fw400s12c686868" sx={{ lineHeight: "20px" }}>
                    {formatDateRange(startDate, endDate)}
                </Typography>
            </Box>

            {/* Chart */}
            <EnergyFlowTrendsView
                startDate={startDate}
                endDate={endDate}
                globalFilters={globalFilters}
                enableDrillDown={true}
            />
        </Box>
    );
};

export default EnergyFlowTrends;